import React from 'react'
import { Box, Button, CircularProgress, Stack, Tooltip } from '@mui/material'

import {
  UI_COLOR_ACCENT_ORANGE,
  UI_COLOR_ACCENT_ORANGE_ALT_HOVER,
  UI_COLOR_DANGER_ALT,
  UI_COLOR_SURFACE_DISABLED_FLAT,
  UI_COLOR_TEXT_DISABLED_FLAT,
  UI_COLOR_WHITE
} from '../../constants/uiColors'
import { UI_TEXT_CANCEL } from '../../constants/uiText'
import GoogleIcon from '../icon/GoogleIcon'

const DISABLED_ACTION_COLORS = {
  background: UI_COLOR_SURFACE_DISABLED_FLAT,
  border: UI_COLOR_SURFACE_DISABLED_FLAT,
  text: UI_COLOR_TEXT_DISABLED_FLAT
}

const DEFAULT_SECONDARY_ACTION = {
  label: UI_TEXT_CANCEL,
  variant: 'outlined',
  icon: 'close',
  color: UI_COLOR_DANGER_ALT
}

const DEFAULT_PRIMARY_ACTION = {
  label: 'Guardar',
  variant: 'contained',
  icon: 'check_circle',
  iconFilled: true,
  color: UI_COLOR_ACCENT_ORANGE,
  hoverColor: UI_COLOR_ACCENT_ORANGE_ALT_HOVER,
  textColor: UI_COLOR_WHITE
}

const getActionButtonSx = ({
  color,
  hoverColor,
  hoverBackgroundColor,
  hoverTextColor,
  textColor,
  variant,
  minWidth,
  width,
  borderWidth,
  disabledColors,
  sx
}) => {
  const isContained = variant === 'contained'
  const resolvedHoverColor = hoverColor || color
  const resolvedTextColor = textColor || (isContained ? UI_COLOR_WHITE : color)
  const resolvedHoverBackgroundColor = hoverBackgroundColor || resolvedHoverColor
  const resolvedHoverTextColor =
    hoverTextColor || (isContained ? resolvedTextColor : UI_COLOR_WHITE)

  return {
    minHeight: 36,
    minWidth,
    width,
    display: 'flex',
    padding: '6px 16px',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    border: `${borderWidth}px solid ${color}`,
    borderRadius: 1,
    backgroundColor: isContained ? color : UI_COLOR_WHITE,
    color: resolvedTextColor,
    boxShadow: 'none',
    fontSize: 13,
    fontWeight: 700,
    lineHeight: 1.2,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    transition:
      'background-color 160ms ease, border-color 160ms ease, color 160ms ease, box-shadow 160ms ease',
    '&:hover': {
      backgroundColor: resolvedHoverBackgroundColor,
      borderColor: resolvedHoverColor,
      color: resolvedHoverTextColor,
      boxShadow: 'none'
    },
    '&:focus-visible': {
      outline: `3px solid ${color}`,
      outlineOffset: 2
    },
    '&.Mui-disabled': {
      backgroundColor: disabledColors.background,
      borderColor: disabledColors.border,
      color: disabledColors.text,
      opacity: 1
    },
    '& .dual-action-buttons__content': {
      minWidth: 0,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 1
    },
    '& .dual-action-buttons__label': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    },
    '& .dual-action-buttons__label-swap': {
      display: 'grid',
      justifyItems: 'center',
      alignItems: 'center'
    },
    '& .dual-action-buttons__label-slot': {
      gridArea: '1 / 1',
      whiteSpace: 'nowrap'
    },
    '& .dual-action-buttons__label-slot[aria-hidden="true"]': {
      visibility: 'hidden'
    },
    '& .MuiCircularProgress-root': {
      color: 'inherit'
    },
    '& .material-symbols-outlined': {
      color: 'inherit'
    },
    ...sx
  }
}

const renderActionIcon = (action) => {
  if (action.loading) {
    if (action.showLoadingIcon === false) return null
    if (action.loadingIconNode) return action.loadingIconNode
    const hasIdleIcon = Boolean(action.iconNode || action.icon)
    if (action.showLoadingIcon !== true && !hasIdleIcon) return null
    return <CircularProgress size={action.iconSize || 20} thickness={4.5} />
  }

  if (action.iconNode) return action.iconNode
  if (!action.icon) return null

  return (
    <GoogleIcon
      name={action.icon}
      size={action.iconSize || 22}
      filled={Boolean(action.iconFilled)}
      color="inherit"
    />
  )
}

const renderActionButton = ({
  action,
  globalDisabled,
  minWidth,
  fullWidthOnMobile,
  equalWidthOnMobile
}) => {
  const {
    label,
    loadingLabel,
    iconPosition = 'start',
    color,
    hoverColor,
    hoverBackgroundColor,
    hoverTextColor,
    textColor,
    variant,
    borderWidth = 1,
    disabled = false,
    loading = false,
    onClick,
    type = 'button',
    tooltip = '',
    ariaLabel,
    disabledColors = DISABLED_ACTION_COLORS,
    sx,
    buttonProps = {}
  } = action
  const { sx: buttonPropsSx, ...muiButtonProps } = buttonProps
  const actionIcon = renderActionIcon(action)
  const width = equalWidthOnMobile
    ? { xs: 'auto', md: action.fullWidth ? '100%' : 'auto' }
    : action.fullWidth
      ? '100%'
      : { xs: fullWidthOnMobile ? '100%' : 'auto', md: 'auto' }
  const resolvedMinWidth = equalWidthOnMobile ? { xs: 0, md: minWidth } : minWidth
  const isDisabled = globalDisabled || disabled || loading
  const resolvedLabel = loading && loadingLabel ? loadingLabel : label
  const resolvedDisabledColors = { ...DISABLED_ACTION_COLORS, ...disabledColors }
  const labelNode = loadingLabel ? (
    <span className="dual-action-buttons__label-swap">
      <span className="dual-action-buttons__label-slot" aria-hidden={loading}>
        {label}
      </span>
      <span className="dual-action-buttons__label-slot" aria-hidden={!loading}>
        {loadingLabel}
      </span>
    </span>
  ) : (
    resolvedLabel
  )

  const button = (
    <Button
      {...muiButtonProps}
      type={type}
      variant={variant}
      disabled={isDisabled}
      onClick={onClick}
      aria-label={ariaLabel || resolvedLabel}
      aria-busy={loading || undefined}
      disableElevation
      sx={getActionButtonSx({
        color,
        hoverColor,
        hoverBackgroundColor,
        hoverTextColor,
        textColor,
        variant,
        minWidth: resolvedMinWidth,
        width,
        borderWidth,
        disabledColors: resolvedDisabledColors,
        sx: {
          ...(equalWidthOnMobile ? { flex: { xs: '1 1 0', md: '0 0 auto' } } : {}),
          ...sx,
          ...buttonPropsSx
        }
      })}
    >
      <span className="dual-action-buttons__content">
        {iconPosition === 'start' && actionIcon}
        <span className="dual-action-buttons__label">{labelNode}</span>
        {iconPosition === 'end' && actionIcon}
      </span>
    </Button>
  )

  if (!tooltip) return button

  return (
    <Tooltip title={tooltip}>
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          width
        }}
      >
        {button}
      </Box>
    </Tooltip>
  )
}

/*
 * Ejemplo de uso:
 * <DualActionButtons
 *   secondaryAction={{ label: 'Cancelar', color: UI_COLOR_DANGER_SOFT, onClick: onCancel }}
 *   primaryAction={{ label: 'Guardar', color: UI_COLOR_PRIMARY, type: 'submit' }}
 * />
 *
 * secondaryAction renderiza el boton izquierdo; primaryAction renderiza el derecho.
 */
export default function DualActionButtons({
  secondaryAction = {},
  primaryAction = {},
  disabled = false,
  gap = 1.25,
  stackOnMobile = false,
  fullWidthOnMobile = true,
  equalWidthOnMobile = false,
  minWidth = 150,
  sx
}) {
  const actions = [
    { ...DEFAULT_SECONDARY_ACTION, ...secondaryAction },
    { ...DEFAULT_PRIMARY_ACTION, ...primaryAction }
  ].filter((action) => action.visible !== false)

  return (
    <Stack
      direction={{ xs: stackOnMobile ? 'column' : 'row', md: 'row' }}
      spacing={gap}
      useFlexGap
      sx={{
        width: { xs: fullWidthOnMobile || equalWidthOnMobile ? '100%' : 'auto', md: 'auto' },
        alignItems: { xs: fullWidthOnMobile || stackOnMobile ? 'stretch' : 'center', md: 'center' },
        justifyContent: 'flex-end',
        flexWrap: {
          xs: equalWidthOnMobile || stackOnMobile ? 'nowrap' : 'wrap',
          md: stackOnMobile ? 'nowrap' : 'wrap'
        },
        ...sx
      }}
    >
      {actions.map((action, index) => (
        <React.Fragment key={action.key || `${action.label}-${index}`}>
          {renderActionButton({
            action,
            globalDisabled: disabled,
            minWidth: action.minWidth || minWidth,
            fullWidthOnMobile,
            equalWidthOnMobile
          })}
        </React.Fragment>
      ))}
    </Stack>
  )
}
